import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  fetchCustomerServiceHistory,
  CustomerServiceHistorySummary,
  CustomerServiceVisit,
} from "../../services/customerServiceHistoryApi";
import { statusBadgeClass } from "./jobCardActivityModuleConfig";
import {
  HistoryPersonIcon,
  SortTimeAscIcon,
  SortTimeDescIcon,
} from "./HistoryIcons";
import { VehicleServiceHistoryDrawer } from "./VehicleServiceHistoryDrawer";

const TIMELINE_GUTTER_PX = 40;
const TIMELINE_LINE_LEFT = TIMELINE_GUTTER_PX / 2;

export type CustomerHistoryVehicleTarget = {
  vehicleNo?: string | null;
  vinNo?: string | null;
};

export interface CustomerServiceHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  customerId?: string | null;
  vehicleNo?: string | null;
  vinNo?: string | null;
  rowSnapshot?: Record<string, any> | null;
  /** drawer = standalone portal; embedded = panel inside parent drawer */
  variant?: "drawer" | "embedded";
  /** For embedded: whether this tab is currently visible */
  isActive?: boolean;
  /**
   * When set (e.g. combined Service Timeline), parent switches to the vehicle tab.
   * When omitted and enableVehicleTimelineLink is true, this drawer opens vehicle history itself.
   */
  onOpenVehicleTimeline?: (target: CustomerHistoryVehicleTarget) => void;
  /**
   * When false, vehicle labels are plain text (not clickable).
   * Gate with Vehicle Timeline permission (Module 11 / screen 2301).
   */
  enableVehicleTimelineLink?: boolean;
}

function visitTimeMs(visit: CustomerServiceVisit): number {
  const raw = visit.date_in || "";
  if (!raw) return 0;
  const d = new Date(raw.includes("T") ? raw : raw.replace(" ", "T"));
  const t = d.getTime();
  return Number.isNaN(t) ? 0 : t;
}

function formatDateTime(iso: string): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatInvoiceAmount(amount: number | null | undefined): string {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch {
    return `₹${Number(amount).toLocaleString()}`;
  }
}

function summaryFromRow(
  row: Record<string, any> | null | undefined
): CustomerServiceHistorySummary {
  return {
    customer_id: String(row?.id ?? row?.customer_id ?? row?.customerId ?? ""),
    customer_name: String(row?.customername ?? row?.customer_name ?? "—"),
    customer_code: String(row?.code_no ?? row?.code_number ?? row?.customer_code ?? "—"),
    contact_number: String(row?.mobile ?? row?.contact_number ?? row?.phone ?? "—"),
    total_visits: 0,
    total_vehicles: 0,
    total_invoiced: 0,
    outstanding: 0,
  };
}

function displayOrDash(value: string | null | undefined): string {
  const s = (value ?? "").trim();
  return s || "—";
}

const MetaRow: React.FC<{
  label: string;
  value: React.ReactNode;
  className?: string;
  wrap?: boolean;
}> = ({ label, value, className = "", wrap = false }) => (
  <div className={`flex items-baseline gap-1.5 min-w-0 text-[11px] leading-snug ${className}`}>
    <span className="shrink-0 text-slate-400 dark:text-gray-500">{label}</span>
    <span
      className={`min-w-0 text-slate-700 dark:text-gray-300 ${
        wrap ? "break-words [overflow-wrap:anywhere]" : "truncate"
      }`}
    >
      {value}
    </span>
  </div>
);

const VisitCard: React.FC<{
  visit: CustomerServiceVisit;
  expanded: boolean;
  onToggle: () => void;
  onVehicleClick?: (target: CustomerHistoryVehicleTarget) => void;
}> = ({ visit, expanded, onToggle, onVehicleClick }) => {
  const serviceLine = [
    visit.service_type || null,
    visit.visit_number ? `Visit #${visit.visit_number}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const vehiclePill =
    (visit.vehicle_label || "").trim() ||
    [visit.vehicle_no, visit.vin_no].filter(Boolean).join(" · ");

  const linkVehicleNo =
    (visit.vehicle_no && String(visit.vehicle_no).trim()) ||
    (visit.vehicle_label
      ? String(visit.vehicle_label).split("·")[0].trim()
      : "") ||
    null;
  const linkVinNo =
    (visit.vin_no && String(visit.vin_no).trim()) || null;

  const canOpenVehicle = Boolean(
    onVehicleClick && (linkVehicleNo || linkVinNo)
  );

  return (
    <div className="relative flex gap-2 mb-3">
      <div
        className="relative z-10 flex shrink-0 justify-center pt-1.5"
        style={{ width: TIMELINE_GUTTER_PX }}
      >
        <div className="w-2.5 h-2.5 rounded-full bg-primary-500/90 ring-[3px] ring-slate-50 dark:ring-gray-900" />
      </div>
      <div className="flex-1 min-w-0 rounded-lg border border-slate-200/70 bg-white/55 dark:bg-gray-800/45 dark:border-gray-700/60 px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex items-baseline gap-2">
            <span className="text-sm font-semibold text-slate-800 dark:text-gray-200 truncate">
              {displayOrDash(visit.jobcard_no)}
            </span>
            {serviceLine ? (
              <span className="text-[10px] text-slate-400 dark:text-gray-500 truncate">
                {serviceLine}
              </span>
            ) : null}
          </div>
          {visit.status ? (
            <span
              className={`shrink-0 px-1.5 py-px rounded text-[9px] font-medium uppercase tracking-wide opacity-90 ${statusBadgeClass(
                visit.status
              )}`}
            >
              {visit.status}
            </span>
          ) : null}
        </div>

        {vehiclePill ? (
          canOpenVehicle ? (
            <button
              type="button"
              onClick={() =>
                onVehicleClick?.({
                  vehicleNo: linkVehicleNo,
                  vinNo: linkVinNo,
                })
              }
              title="Open vehicle timeline"
              className="mt-1 inline-flex items-center gap-1 max-w-full text-[10px] font-medium text-primary-700 dark:text-primary-300 hover:underline"
            >
              <svg
                className="w-2.5 h-2.5 shrink-0 opacity-80"
                viewBox="0 0 16 16"
                fill="currentColor"
                aria-hidden
              >
                <path d="M4 9a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm10 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM6 2a.5.5 0 0 0-.5.5V4H2.5A1.5 1.5 0 0 0 1 5.5v5A1.5 1.5 0 0 0 2.5 12H3a2 2 0 1 0 4 0h2a2 2 0 1 0 4 0h.5A1.5 1.5 0 0 0 15 10.5v-5A1.5 1.5 0 0 0 13.5 4H10.5V2.5A.5.5 0 0 0 10 2H6zm6.5 3a.5.5 0 0 1 .5.5v1H3V5.5a.5.5 0 0 1 .5-.5h9z" />
              </svg>
              <span className="truncate">{vehiclePill}</span>
            </button>
          ) : (
            <div className="mt-1 inline-flex items-center gap-1 max-w-full text-[10px] text-slate-500 dark:text-gray-400">
              <svg
                className="w-2.5 h-2.5 shrink-0 opacity-70"
                viewBox="0 0 16 16"
                fill="currentColor"
                aria-hidden
              >
                <path d="M4 9a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm10 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM6 2a.5.5 0 0 0-.5.5V4H2.5A1.5 1.5 0 0 0 1 5.5v5A1.5 1.5 0 0 0 2.5 12H3a2 2 0 1 0 4 0h2a2 2 0 1 0 4 0h.5A1.5 1.5 0 0 0 15 10.5v-5A1.5 1.5 0 0 0 13.5 4H10.5V2.5A.5.5 0 0 0 10 2H6zm6.5 3a.5.5 0 0 1 .5.5v1H3V5.5a.5.5 0 0 1 .5-.5h9z" />
              </svg>
              <span className="truncate">{vehiclePill}</span>
            </div>
          )
        ) : null}

        <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5">
          <MetaRow label="In" value={formatDateTime(visit.date_in)} />
          <MetaRow label="Out" value={formatDateTime(visit.date_out)} />
          <MetaRow label="Salesman" value={displayOrDash(visit.salesman)} />
          <MetaRow label="Receiver" value={displayOrDash(visit.receiver_name)} />
          <MetaRow
            label="Invoice"
            value={
              <span className="font-medium text-slate-800 dark:text-gray-200">
                {formatInvoiceAmount(visit.invoice_amount)}
              </span>
            }
          />
        </div>

        <MetaRow
          className="mt-1"
          label="Complaint"
          wrap
          value={
            <span className="whitespace-pre-wrap">
              {displayOrDash(visit.customer_complaint)}
            </span>
          }
        />

        {expanded ? (
          <div className="mt-2 pt-2 border-t border-dashed border-slate-200/80 dark:border-gray-600/70 space-y-2">
            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[10px] font-medium text-slate-500 dark:text-gray-400">
                  Service Progress
                </span>
                {visit.status ? (
                  <span className="text-[9px] font-medium text-emerald-700/80 dark:text-emerald-300/80">
                    {visit.status}
                  </span>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-1">
                {(visit.service_progress || []).length > 0 ? (
                  visit.service_progress.map((step) => (
                    <span
                      key={step}
                      className="inline-flex items-center gap-0.5 px-1.5 py-px rounded bg-emerald-50/80 text-emerald-800/90 dark:bg-emerald-900/25 dark:text-emerald-200/90 text-[9px] font-medium"
                    >
                      <span aria-hidden>✓</span>
                      {step}
                    </span>
                  ))
                ) : (
                  <span className="text-[11px] text-slate-400">—</span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
              <MetaRow
                label="Parts"
                value={
                  visit.parts_issued_count > 0
                    ? `${visit.parts_issued_count} Issues`
                    : "—"
                }
              />
              <MetaRow
                label="QC"
                value={[visit.qc_status, visit.qc_ref].filter(Boolean).join(" · ") || "—"}
              />
              <MetaRow
                label="Payment"
                value={displayOrDash(visit.payment_status || undefined)}
              />
              <MetaRow label="Gate Pass" value={displayOrDash(visit.gate_pass_no)} />
              <MetaRow
                className="col-span-2"
                label="Recv. Mobile"
                value={displayOrDash(visit.receiver_mobile)}
              />
            </div>
          </div>
        ) : null}

        <div className="mt-1.5 flex justify-end">
          <button
            type="button"
            onClick={onToggle}
            className="text-[11px] font-medium text-primary-700/90 dark:text-primary-300/90 hover:underline inline-flex items-center gap-0.5"
          >
            {expanded ? (
              <>
                Hide Details
                <span aria-hidden className="text-[9px]">
                  ▲
                </span>
              </>
            ) : (
              <>
                View Details
                <span aria-hidden>→</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export const CustomerServiceHistoryDrawer: React.FC<
  CustomerServiceHistoryDrawerProps
> = ({
  isOpen,
  onClose,
  customerId,
  vehicleNo,
  vinNo,
  rowSnapshot,
  variant = "drawer",
  isActive = true,
  onOpenVehicleTimeline,
  enableVehicleTimelineLink = false,
}) => {
  const embedded = variant === "embedded";
  const [summary, setSummary] = useState<CustomerServiceHistorySummary | null>(
    null
  );
  const [visits, setVisits] = useState<CustomerServiceVisit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** false = newest first (default Desc) */
  const [sortAsc, setSortAsc] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const [vehicleTimelineTarget, setVehicleTimelineTarget] =
    useState<CustomerHistoryVehicleTarget | null>(null);
  const loadedKeyRef = React.useRef<string | null>(null);

  const handleVehicleClick = useCallback(
    (target: CustomerHistoryVehicleTarget) => {
      if (!enableVehicleTimelineLink) return;
      if (onOpenVehicleTimeline) {
        onOpenVehicleTimeline(target);
        return;
      }
      setVehicleTimelineTarget(target);
    },
    [enableVehicleTimelineLink, onOpenVehicleTimeline]
  );

  useEffect(() => {
    if (!isOpen) setVehicleTimelineTarget(null);
  }, [isOpen]);

  const instantSummary = useMemo(() => summaryFromRow(rowSnapshot), [rowSnapshot]);
  const hasKey = Boolean(
    (customerId && String(customerId).trim()) ||
      (vehicleNo && String(vehicleNo).trim()) ||
      (vinNo && String(vinNo).trim())
  );
  const loadKey = `${customerId || ""}|${vehicleNo || ""}|${vinNo || ""}`;

  const sortedVisits = useMemo(() => {
    const copy = [...visits];
    copy.sort((a, b) => {
      const diff = visitTimeMs(a) - visitTimeMs(b);
      if (diff !== 0) return sortAsc ? diff : -diff;
      const idA = String(a.jobcard_id || a.jobcard_no || "");
      const idB = String(b.jobcard_id || b.jobcard_no || "");
      return sortAsc ? idA.localeCompare(idB) : idB.localeCompare(idA);
    });
    return copy;
  }, [visits, sortAsc]);

  const load = useCallback(async () => {
    if (!hasKey) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchCustomerServiceHistory({
        customer_id: customerId,
        vehicle_no: vehicleNo,
        vin_no: vinNo,
      });
      if (res?.success === false) {
        setError(res.message || "Could not load customer history.");
        setVisits([]);
        setSummary(instantSummary);
        return;
      }
      setSummary(res.summary ?? instantSummary);
      setVisits(Array.isArray(res.visits) ? res.visits : []);
      setExpandedIds({});
      loadedKeyRef.current = loadKey;
    } catch (e: any) {
      setError(e?.message || "Failed to load customer history.");
      setSummary(instantSummary);
      setVisits([]);
    } finally {
      setLoading(false);
    }
  }, [hasKey, customerId, vehicleNo, vinNo, instantSummary, loadKey]);

  useEffect(() => {
    if (!isOpen) {
      loadedKeyRef.current = null;
      return;
    }
    if (embedded && !isActive) return;
    if (!hasKey) {
      setVisits([]);
      setSummary(instantSummary);
      setError(null);
      return;
    }
    if (embedded && loadedKeyRef.current === loadKey) return;
    setVisits([]);
    setSummary(instantSummary);
    setSortAsc(false);
    setExpandedIds({});
    void load();
  }, [isOpen, isActive, customerId, vehicleNo, vinNo, hasKey, embedded, loadKey]);

  useEffect(() => {
    if (!isOpen || embedded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, embedded]);

  if (!isOpen) return null;
  if (!embedded && !hasKey) return null;

  const displaySummary = summary ?? instantSummary;
  const customerLabel =
    displaySummary.customer_name && displaySummary.customer_name !== "—"
      ? displaySummary.customer_name
      : rowSnapshot?.customername ||
        rowSnapshot?.customer_name ||
        "—";

  const content = (
    <div className="h-full overflow-y-auto p-5 custom-scrollbar">
      {!hasKey ? (
        <p className="text-sm text-slate-500 dark:text-gray-400 py-8 text-center">
          No customer available for this row.
        </p>
      ) : (
        <>
          <div className="rounded-xl border border-black/5 bg-gradient-to-br from-white to-slate-100 dark:from-gray-800 dark:to-gray-900 p-4 mb-5 shadow-sm">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 text-[11px]">
              <div>
                <div className="text-slate-500">Customer Code</div>
                <div className="font-bold text-sm text-slate-900 dark:text-gray-100">
                  {displayOrDash(displaySummary.customer_code)}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Contact Number</div>
                <div className="font-bold text-sm text-slate-900 dark:text-gray-100">
                  {displayOrDash(displaySummary.contact_number)}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Total Visits</div>
                <div className="font-bold text-sm text-slate-900 dark:text-gray-100">
                  {typeof displaySummary.total_visits === "number"
                    ? `${displaySummary.total_visits} Visit${
                        displaySummary.total_visits === 1 ? "" : "s"
                      }`
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Total Vehicles</div>
                <div className="font-bold text-sm text-slate-900 dark:text-gray-100">
                  {typeof displaySummary.total_vehicles === "number"
                    ? `${displaySummary.total_vehicles} Vehicle${
                        displaySummary.total_vehicles === 1 ? "" : "s"
                      }`
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Total Invoiced</div>
                <div className="font-bold text-sm text-emerald-700 dark:text-emerald-300">
                  {formatInvoiceAmount(displaySummary.total_invoiced)}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Outstanding</div>
                <div className="font-bold text-sm text-red-600 dark:text-red-400">
                  {formatInvoiceAmount(displaySummary.outstanding)}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 mb-3">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-gray-200 m-0">
              Customer Visits ({sortAsc ? "Oldest → Newest" : "Newest → Oldest"})
            </h3>
            <button
              type="button"
              onClick={() => setSortAsc((v) => !v)}
              title={
                sortAsc
                  ? "Showing oldest first — click for newest first"
                  : "Showing newest first — click for oldest first"
              }
              aria-label={sortAsc ? "Sort by date descending" : "Sort by date ascending"}
              className="inline-flex items-center gap-1 shrink-0 px-2 py-1 rounded-md border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800 hover:text-primary-700 dark:hover:text-primary-300 transition-colors text-xs font-medium"
            >
              {sortAsc ? (
                <SortTimeAscIcon className="w-4 h-4" />
              ) : (
                <SortTimeDescIcon className="w-4 h-4" />
              )}
              <span>{sortAsc ? "Asc" : "Desc"}</span>
            </button>
          </div>

          {error ? (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-800 dark:text-red-200 flex items-center justify-between gap-2">
              <span>{error}</span>
              <button
                type="button"
                className="text-xs font-semibold underline shrink-0"
                onClick={() => {
                  loadedKeyRef.current = null;
                  void load();
                }}
              >
                Retry
              </button>
            </div>
          ) : null}

          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-slate-500">
              <span className="inline-block h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mr-2" />
              Loading customer history…
            </div>
          ) : visits.length === 0 && !error ? (
            <p className="text-sm text-slate-500 dark:text-gray-400 py-8 text-center">
              No service history found for this customer.
            </p>
          ) : (
            <div className="relative">
              <div
                className="absolute top-1 bottom-1 w-[3px] rounded-full pointer-events-none"
                style={{
                  left: TIMELINE_LINE_LEFT,
                  transform: "translateX(-50%)",
                  background:
                    "linear-gradient(to bottom, var(--color-primary-600, #2ea04a), var(--color-primary-200, #bbf7d0))",
                }}
                aria-hidden
              />
              {sortedVisits.map((visit) => {
                const key = visit.jobcard_id || visit.jobcard_no;
                return (
                  <VisitCard
                    key={key}
                    visit={visit}
                    expanded={Boolean(expandedIds[key])}
                    onToggle={() =>
                      setExpandedIds((prev) => ({
                        ...prev,
                        [key]: !prev[key],
                      }))
                    }
                    onVehicleClick={
                      enableVehicleTimelineLink ? handleVehicleClick : undefined
                    }
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );

  const nestedVehicleDrawer =
    enableVehicleTimelineLink &&
    !onOpenVehicleTimeline &&
    vehicleTimelineTarget ? (
      <VehicleServiceHistoryDrawer
        isOpen
        onClose={() => setVehicleTimelineTarget(null)}
        vehicleNo={vehicleTimelineTarget.vehicleNo}
        vinNo={vehicleTimelineTarget.vinNo}
        rowSnapshot={{
          ...(rowSnapshot || {}),
          platenumber:
            vehicleTimelineTarget.vehicleNo ||
            rowSnapshot?.platenumber ||
            rowSnapshot?.plate_number,
          vin_no:
            vehicleTimelineTarget.vinNo ||
            rowSnapshot?.vin_no ||
            rowSnapshot?.vin_number,
        }}
      />
    ) : null;

  if (embedded) {
    return (
      <>
        {content}
        {nestedVehicleDrawer}
      </>
    );
  }

  const body = (
    <>
      <div
        className="fixed inset-0 z-[11999] bg-black/50 backdrop-blur-sm"
        aria-hidden
        onClick={onClose}
      />
      <div
        className="fixed top-0 right-0 z-[12000] h-full w-full max-w-[560px] bg-slate-50 dark:bg-gray-900 shadow-2xl flex flex-col border-l border-gray-200 dark:border-gray-700 transform transition-transform duration-300"
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-service-history-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-5 py-5 text-white bg-gradient-to-br from-primary-800 to-primary-500">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2
                id="customer-service-history-drawer-title"
                className="text-base font-bold m-0 flex items-center gap-2"
              >
                <HistoryPersonIcon className="w-4 h-4 opacity-90" />
                Customer Service History
              </h2>
              <p className="text-xs opacity-90 mt-0.5">Customer: {customerLabel}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-white/90 hover:text-white text-2xl leading-none px-1"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">{content}</div>
      </div>
      {nestedVehicleDrawer}
    </>
  );

  if (typeof document === "undefined") return null;
  return createPortal(body, document.body);
};

export default CustomerServiceHistoryDrawer;
